import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Config } from '../../config';
import Sidebar from '../../partials/Sidebar';
import Header from '../../partials/Header';

const PAGE_SIZE = 25;

function EventViewer() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activityTypes, setActivityTypes] = useState([]);
  const [filters, setFilters] = useState({
    activity_type: '',
    user_id: '',
    date_from: '',
    date_to: '',
  });
  const [appliedFilters, setAppliedFilters] = useState({});
  const [page, setPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'access_token': `Bearer ${token}` };

  // Fetch available activity types on mount
  useEffect(() => {
    axios.get(`${Config.APIBaseURL}/admin/audit-logs/activity-types`, {
      headers,
      withCredentials: true
    })
      .then(res => setActivityTypes(res.data || []))
      .catch(() => {});
  }, []);

  const loadEvents = useCallback(() => {
    setLoading(true);
    setError(null);

    const params = { skip: page * PAGE_SIZE, limit: PAGE_SIZE };
    if (appliedFilters.activity_type) params.activity_type = appliedFilters.activity_type;
    if (appliedFilters.user_id !== '' && appliedFilters.user_id != null) {
      const uid = parseInt(appliedFilters.user_id, 10);
      if (!Number.isNaN(uid)) params.user_id = uid;
    }
    if (appliedFilters.date_from) params.date_from = appliedFilters.date_from;
    if (appliedFilters.date_to) params.date_to = appliedFilters.date_to;

    axios.get(`${Config.APIBaseURL}/admin/audit-logs`, {
      headers,
      params,
      withCredentials: true
    })
      .then(res => {
        setEvents(res.data?.results || []);
        setTotal(res.data?.total || 0);
      })
      .catch(e => {
        setError(e.response?.data?.detail || 'Failed to load events');
        setEvents([]);
      })
      .finally(() => setLoading(false));
  }, [page, appliedFilters]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleApplyFilters = () => {
    setAppliedFilters({ ...filters });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({ activity_type: '', user_id: '', date_from: '', date_to: '' });
    setAppliedFilters({});
    setPage(0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleApplyFilters();
  };

  const formatEventTime = (iso) => {
    if (!iso) return '\u2014';
    try {
      const d = new Date(iso);
      return d.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });
    } catch {
      return iso;
    }
  };

  const getActivityBadgeColor = (type) => {
    if (!type) return 'bg-gray-100 text-gray-700';
    if (type.includes('login_failed') || type.includes('access_denied')) return 'bg-red-100 text-red-700';
    if (type.includes('login') || type.includes('signup')) return 'bg-green-100 text-green-700';
    if (type.includes('deleted') || type.includes('delete')) return 'bg-red-100 text-red-700';
    if (type.includes('created') || type.includes('create')) return 'bg-blue-100 text-blue-700';
    if (type.includes('updated') || type.includes('update')) return 'bg-yellow-100 text-yellow-700';
    if (type.includes('password')) return 'bg-orange-100 text-orange-700';
    if (type.includes('report') || type.includes('import')) return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
          {/* Page header */}
          <div className="mb-8">
            <h1 className="text-2xl text-slate-800 dark:text-slate-100 font-bold">Event Viewer</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Review security events, user actions, and system activity.
            </p>
          </div>

          {/* Filters */}
          <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700 p-4 mb-6">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Activity Type</label>
                <select
                  value={filters.activity_type}
                  onChange={(e) => setFilters(f => ({ ...f, activity_type: e.target.value }))}
                  className="w-full border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded px-2 py-1.5 text-sm"
                >
                  <option value="">All</option>
                  {activityTypes.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="w-24">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">User ID</label>
                <input
                  type="text"
                  placeholder="\u2014"
                  value={filters.user_id}
                  onChange={(e) => setFilters(f => ({ ...f, user_id: e.target.value }))}
                  onKeyDown={handleKeyDown}
                  className="w-full border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">From</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))}
                  className="border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">To</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters(f => ({ ...f, date_to: e.target.value }))}
                  className="border border-slate-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded px-2 py-1.5 text-sm"
                />
              </div>
              <button
                type="button"
                onClick={handleApplyFilters}
                className="btn bg-indigo-500 hover:bg-indigo-600 text-white text-sm px-4 py-1.5 rounded"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={handleClearFilters}
                className="btn border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 text-sm px-4 py-1.5 rounded hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Clear
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-sm p-3 mb-4">
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Results count */}
          <div className="mb-2 text-sm text-slate-500 dark:text-slate-400">
            {total > 0 ? `${total} event${total !== 1 ? 's' : ''} found` : ''}
          </div>

          {/* Table */}
          <div className="bg-white dark:bg-slate-800 shadow-lg rounded-sm border border-slate-200 dark:border-slate-700">
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 font-medium">Time</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">User</th>
                    <th className="px-4 py-3 font-medium">IP Address</th>
                    <th className="px-4 py-3 font-medium">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                        Loading events...
                      </td>
                    </tr>
                  ) : events.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                        No events match the current filters.
                      </td>
                    </tr>
                  ) : (
                    events.map((ev) => (
                      <React.Fragment key={ev.id}>
                        <tr
                          className="hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer"
                          onClick={() => setExpandedRow(expandedRow === ev.id ? null : ev.id)}
                        >
                          <td className="px-4 py-2.5 whitespace-nowrap text-slate-700 dark:text-slate-300">
                            {formatEventTime(ev.timestamp)}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${getActivityBadgeColor(ev.activity_type)}`}>
                              {ev.activity_type}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">
                            {ev.username || (ev.user_id != null ? `#${ev.user_id}` : '\u2014')}
                          </td>
                          <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 font-mono text-xs">
                            {ev.ip_address || '\u2014'}
                          </td>
                          <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 max-w-[250px] truncate">
                            {ev.details ? (
                              typeof ev.details === 'object' ? JSON.stringify(ev.details) : String(ev.details)
                            ) : '\u2014'}
                          </td>
                        </tr>
                        {expandedRow === ev.id && ev.details && (
                          <tr className="bg-slate-50 dark:bg-slate-900/30">
                            <td colSpan={5} className="px-4 py-3">
                              <div className="text-xs font-mono text-slate-600 dark:text-slate-300 whitespace-pre-wrap break-all">
                                {JSON.stringify(ev.details, null, 2)}
                              </div>
                              {ev.user_agent && (
                                <div className="mt-2 text-xs text-slate-400 truncate">
                                  <span className="font-medium">User Agent:</span> {ev.user_agent}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0 || loading}
                  className="text-sm text-indigo-500 hover:text-indigo-600 disabled:opacity-40 disabled:pointer-events-none"
                >
                  &larr; Previous
                </button>
                <span className="text-sm text-slate-500 dark:text-slate-400">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page + 1 >= totalPages || loading}
                  className="text-sm text-indigo-500 hover:text-indigo-600 disabled:opacity-40 disabled:pointer-events-none"
                >
                  Next &rarr;
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventViewer;
